// Responsive admin shell: sidebar + topbar on desktop, drawer on mobile.
//
// Brand language: dark surfaces, teal-mint gradient accents, subtle
// radial glow on the page background. Tokens live in lib/theme.tsx.

import { Suspense, useEffect, useState } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import { AuthProvider, RequireAuth, useAuth, type AuthUser } from "./lib/auth";
import type { Section } from "./lib/api";
import { tokens } from "./lib/theme";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import ClientNew from "./pages/ClientNew";
import MirrorDashboard from "./pages/MirrorDashboard";
import MirrorClients from "./pages/MirrorClients";
import MirrorClientDetail from "./pages/MirrorClientDetail";
import MirrorClientNew from "./pages/MirrorClientNew";
import Settings from "./pages/Settings";
import Logs from "./pages/Logs";
import LegalNiches from "./pages/legal/LegalNiches";
import LegalNicheNew from "./pages/legal/LegalNicheNew";
import LegalNicheEditor from "./pages/legal/LegalNicheEditor";
import LegalNicheObservability from "./pages/legal/LegalNicheObservability";
import SmsDashboard from "./pages/SmsDashboard";
import SmsAutoSend from "./pages/SmsAutoSend";
import SmsCampaigns from "./pages/SmsCampaigns";
import SmsCampaignNew from "./pages/SmsCampaignNew";
import SmsCampaignDetail from "./pages/SmsCampaignDetail";
import GrtPostback from "./pages/GrtPostback";
import Dialer from "./pages/Dialer";
import LeadSales from "./pages/LeadSales";
import LeadSalesWitzzy from "./pages/LeadSalesWitzzy";
import LeadSalesHtm from "./pages/LeadSalesHtm";
import OutboundCalling from "./pages/OutboundCalling";
import Yes from "./pages/Yes";
import Users from "./pages/Users";
import NoAccess from "./pages/NoAccess";

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  /** Only mark active on an exact path match (used for index-style routes). */
  end?: boolean;
}

interface NavGroup {
  /** Optional section heading; undefined renders the items ungrouped. */
  heading?: string;
  /**
   * The dashboard section this group belongs to. NavList shows the group only
   * to admins or to users granted this section. Mutually exclusive with
   * `adminOnly`.
   */
  section?: Section;
  /** When true, the group is admin-only (the "Admin" group: Users + Settings). */
  adminOnly?: boolean;
  items: NavItem[];
}

// The six dashboard sections in nav/priority order, each mapped to its landing
// path — the single source of truth for both the first-granted landing redirect
// and the NAV group tags below. Order here IS the section priority order used by
// firstGrantedPath(); the NAV groups are declared in the same order.
const SECTION_ORDER: Section[] = [
  "debt-lookup",
  "inbound-rtb",
  "legal-widget",
  "sms",
  "logs",
  "reporting",
  "dialer",
  "lead-sales",
  "yes",
];

const SECTION_LANDING: Record<Section, string> = {
  "debt-lookup": "/debt-lookup",
  "inbound-rtb": "/inbound-rtb",
  "legal-widget": "/legal",
  sms: "/sms",
  logs: "/logs",
  reporting: "/grt-postback",
  dialer: "/dialer",
  "lead-sales": "/lead-sales",
  yes: "/yes",
};

// Where a user lands on "/" and any unmatched route: an admin always goes to
// Debt Lookup; everyone else to the first section (in priority order) they hold;
// a zero-grant user to the NoAccess page. Only ever returns a path the user can
// actually reach, so redirects to it never loop.
function firstGrantedPath(user: AuthUser): string {
  if (user.isAdmin) return "/debt-lookup";
  const first = SECTION_ORDER.find((s) => user.sections.includes(s));
  return first ? SECTION_LANDING[first] : "/no-access";
}

// Navigation is grouped by dashboard section: Debt Lookup (the mirror — the
// primary product, shown first) then Inbound RTB (the original GTI surfaces),
// Legal Widget, SMS, Logs, Reporting. Each group is tagged with its Section id
// so NavList can hide the groups a user hasn't been granted. The trailing
// admin-only "Admin" group (Users + Settings) renders only for admins. Group
// order matches SECTION_ORDER above (the section priority order).
const NAV: NavGroup[] = [
  {
    heading: "Debt Lookup",
    section: "debt-lookup",
    items: [
      { to: "/debt-lookup", label: "Dashboard", icon: <IconDebtLookup />, end: true },
      { to: "/debt-lookup/clients", label: "API keys", icon: <IconClients /> },
    ],
  },
  {
    heading: "Inbound RTB",
    section: "inbound-rtb",
    items: [
      { to: "/inbound-rtb", label: "Dashboard", icon: <IconDashboard />, end: true },
      { to: "/clients", label: "Clients", icon: <IconClients /> },
    ],
  },
  {
    heading: "Legal Widget",
    section: "legal-widget",
    items: [
      { to: "/legal", label: "Niches", icon: <IconLegal />, end: true },
    ],
  },
  {
    heading: "SMS",
    section: "sms",
    items: [
      { to: "/sms", label: "Dashboard", icon: <IconSms />, end: true },
      { to: "/sms/auto-send", label: "Auto Send", icon: <IconAutoSend /> },
      { to: "/sms/campaigns", label: "Campaigns", icon: <IconSms /> },
    ],
  },
  {
    heading: "Logs",
    section: "logs",
    items: [
      { to: "/logs", label: "Errors", icon: <IconLogs />, end: true },
    ],
  },
  {
    heading: "Reporting",
    section: "reporting",
    items: [
      { to: "/grt-postback", label: "GRT Postback", icon: <IconLogs />, end: true },
    ],
  },
  {
    heading: "Dialer",
    section: "dialer",
    items: [
      { to: "/dialer", label: "Dashboard", icon: <IconDialer />, end: true },
    ],
  },
  {
    heading: "Lead Sales",
    section: "lead-sales",
    items: [
      { to: "/lead-sales", label: "Dashboard", icon: <IconLeadSales />, end: true },
      { to: "/lead-sales/witzzy", label: "Outbound", icon: <IconLeadSales /> },
      { to: "/lead-sales/outbound", label: "Outbound calling", icon: <IconLeadSales /> },
      { to: "/lead-sales/htm", label: "HTM", icon: <IconLeadSales /> },
    ],
  },
  {
    heading: "Yes",
    section: "yes",
    items: [
      { to: "/yes", label: "Dashboard", icon: <IconYes />, end: true },
    ],
  },
  {
    heading: "Admin",
    adminOnly: true,
    items: [
      { to: "/users", label: "Users", icon: <IconClients /> },
      { to: "/settings", label: "Settings", icon: <IconSettings /> },
    ],
  },
];

function AppShell({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Inject a tiny global stylesheet once (page background, link reset,
  // shimmer keyframes, scrollbar styling). Cheaper than shipping a CSS
  // file when the rest of the SPA uses inline styles.
  useEffect(() => {
    const id = "loop-rtb-globals";
    if (document.getElementById(id)) return;
    const tag = document.createElement("style");
    tag.id = id;
    tag.textContent = `
      *, *::before, *::after { box-sizing: border-box; }
      html, body, #root { margin: 0; padding: 0; min-height: 100%; }
      body {
        background: ${tokens.gradient.pageBg};
        color: ${tokens.color.fg0};
        font-family: ${tokens.font.family};
        font-size: ${tokens.font.size.base}px;
        line-height: 1.5;
        -webkit-font-smoothing: antialiased;
      }
      a { color: ${tokens.color.tealLight}; text-decoration: none; }
      a:hover { color: ${tokens.color.teal}; }
      input, button, select, textarea {
        font-family: inherit;
        color: inherit;
      }
      input:focus, select:focus, textarea:focus {
        border-color: rgba(0,212,170,0.5) !important;
        box-shadow: 0 0 0 3px rgba(0,212,170,0.15);
      }
      button:focus-visible {
        outline: 2px solid ${tokens.color.tealLight};
        outline-offset: 2px;
      }
      ::-webkit-scrollbar { width: 10px; height: 10px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb {
        background: rgba(255,255,255,0.06);
        border-radius: 4px;
      }
      ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.12); }
      @keyframes loop-rtb-shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after { animation: none !important; transition: none !important; }
      }
    `;
    document.head.appendChild(tag);
  }, []);

  // Close drawer on route change.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  if (location.pathname === "/login") return <>{children}</>;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr)",
        minHeight: "100vh",
      }}
    >
      <DesktopSidebar />
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <div style={mainColumnStyle}>
        <Topbar
          user={user}
          onMenu={() => setDrawerOpen(true)}
          onSignOut={() => void signOut()}
        />
        <main style={mainStyle}>{children}</main>
      </div>
      <ResponsiveCSS />
    </div>
  );
}

const mainColumnStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  minWidth: 0,
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  padding: tokens.space.xl,
  // Full window width (minus the sidebar) — no centered max-width cap, so the
  // data-dense pages (esp. the CV API leads table with 10 columns) use all the
  // available horizontal space. `minWidth: 0` lets the flex child shrink so
  // wide tables get a horizontal scrollbar instead of overflowing the layout.
  width: "100%",
  minWidth: 0,
};

// ---------------------------------------------------------------------------
// Desktop sidebar (hidden < 1024px via media query in <ResponsiveCSS/>)
// ---------------------------------------------------------------------------

function DesktopSidebar() {
  return (
    <aside
      data-sidebar="desktop"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        width: 240,
        background: tokens.color.bg1,
        borderRight: `1px solid ${tokens.color.border}`,
        padding: `${tokens.space.lg}px ${tokens.space.md}px`,
        display: "none",
        flexDirection: "column",
        gap: tokens.space.lg,
        zIndex: 30,
      }}
    >
      <BrandMark />
      <NavList />
      <FooterMark />
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Mobile drawer (visible only when toggled, dim backdrop)
// ---------------------------------------------------------------------------

function MobileDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  return (
    <>
      {open && (
        <div
          aria-hidden
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(2px)",
            zIndex: 40,
          }}
        />
      )}
      <aside
        role="dialog"
        aria-label="Navigation"
        aria-hidden={!open}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          width: 280,
          maxWidth: "85vw",
          background: tokens.color.bg1,
          borderRight: `1px solid ${tokens.color.border}`,
          padding: `${tokens.space.lg}px ${tokens.space.md}px`,
          display: "flex",
          flexDirection: "column",
          gap: tokens.space.lg,
          transform: open ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
          zIndex: 50,
        }}
      >
        <BrandMark onClose={onClose} />
        <NavList />
        <FooterMark />
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------
// Topbar — mobile menu trigger + user/signout
// ---------------------------------------------------------------------------

function Topbar({
  user,
  onMenu,
  onSignOut,
}: {
  user: { username: string; email: string | null } | null;
  onMenu: () => void;
  onSignOut: () => void;
}) {
  return (
    <header
      style={{
        height: 60,
        display: "flex",
        alignItems: "center",
        gap: tokens.space.md,
        padding: `0 ${tokens.space.lg}px`,
        background: tokens.color.bg0,
        borderBottom: `1px solid ${tokens.color.border}`,
        position: "sticky",
        top: 0,
        zIndex: 20,
      }}
    >
      <button
        type="button"
        onClick={onMenu}
        aria-label="Open navigation"
        data-menu-toggle
        style={{
          display: "none",
          width: 40,
          height: 40,
          borderRadius: tokens.radius.sm,
          background: "transparent",
          color: tokens.color.fg1,
          border: `1px solid ${tokens.color.border}`,
          cursor: "pointer",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <IconMenu />
      </button>
      <div style={{ flex: 1 }} />
      {user && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.space.sm,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: tokens.gradient.brand,
              color: tokens.color.bg0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: tokens.font.size.sm,
            }}
            aria-hidden
          >
            {(user.email ?? user.username).slice(0, 1).toUpperCase()}
          </div>
          <div
            style={{
              fontSize: tokens.font.size.sm,
              color: tokens.color.fg1,
              maxWidth: 220,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            data-user-label
          >
            {user.email ?? user.username}
          </div>
          <button
            type="button"
            onClick={onSignOut}
            style={{
              padding: "6px 12px",
              borderRadius: tokens.radius.pill,
              background: "transparent",
              color: tokens.color.fg1,
              border: `1px solid ${tokens.color.borderMid}`,
              cursor: "pointer",
              fontSize: tokens.font.size.sm,
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Brand mark + nav primitives
// ---------------------------------------------------------------------------

function BrandMark({ onClose }: { onClose?: () => void }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: tokens.space.sm,
        padding: `0 ${tokens.space.sm}px`,
      }}
    >
      <Link
        to="/"
        style={{
          display: "flex",
          alignItems: "center",
          gap: tokens.space.sm,
          textDecoration: "none",
        }}
      >
        <img src="/logo.png" alt="Loop" style={{ height: 28, width: "auto" }} />
        <span
          style={{
            fontWeight: 700,
            fontSize: tokens.font.size.lg,
            background: tokens.gradient.brandText,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
            letterSpacing: "-0.01em",
          }}
        >
          Loop&nbsp;RTB
        </span>
      </Link>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close navigation"
          style={{
            background: "transparent",
            color: tokens.color.fg1,
            border: "none",
            cursor: "pointer",
            padding: tokens.space.xs,
          }}
        >
          <IconClose />
        </button>
      )}
    </div>
  );
}

function NavList() {
  const { user } = useAuth();
  // Show a section group to admins or to users granted that section; show the
  // admin-only group to admins only. With no user (signed out / still loading)
  // nothing renders.
  const canSee = (group: NavGroup): boolean => {
    if (!user) return false;
    if (group.adminOnly) return user.isAdmin;
    if (group.section) return user.isAdmin || user.sections.includes(group.section);
    return true;
  };
  const groups = NAV.filter(canSee);
  return (
    <nav
      style={{
        display: "flex",
        flexDirection: "column",
        gap: tokens.space.md,
        flex: 1,
      }}
    >
      {groups.map((group, gi) => (
        <div
          key={group.heading ?? `group-${gi}`}
          style={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          {group.heading && (
            <div
              style={{
                padding: "4px 12px",
                fontSize: tokens.font.size.xs,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: tokens.color.fg2,
                fontWeight: 600,
              }}
            >
              {group.heading}
            </div>
          )}
          {group.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              style={({ isActive }) => ({
                display: "flex",
                alignItems: "center",
                gap: tokens.space.sm,
                padding: "10px 12px",
                borderRadius: tokens.radius.sm,
                color: isActive ? tokens.color.tealLight : tokens.color.fg1,
                background: isActive ? "rgba(0,212,170,0.10)" : "transparent",
                border: isActive
                  ? "1px solid rgba(0,212,170,0.25)"
                  : "1px solid transparent",
                fontWeight: isActive ? 600 : 500,
                fontSize: tokens.font.size.base,
                textDecoration: "none",
                transition: "background 120ms ease, color 120ms ease",
              })}
            >
              <span aria-hidden style={{ display: "inline-flex" }}>
                {item.icon}
              </span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

function FooterMark() {
  return (
    <div
      style={{
        marginTop: "auto",
        padding: `${tokens.space.sm}px ${tokens.space.sm}px 0`,
        borderTop: `1px solid ${tokens.color.border}`,
        color: tokens.color.fg2,
        fontSize: tokens.font.size.xs,
      }}
    >
      Operator console
    </div>
  );
}

// ---------------------------------------------------------------------------
// Responsive CSS (one tag, controls breakpoints for the shell)
// ---------------------------------------------------------------------------

function ResponsiveCSS() {
  useEffect(() => {
    const id = "loop-rtb-shell-responsive";
    if (document.getElementById(id)) return;
    const tag = document.createElement("style");
    tag.id = id;
    tag.textContent = `
      @media (min-width: ${tokens.breakpoint.lg}px) {
        [data-sidebar="desktop"] { display: flex !important; }
        [data-menu-toggle] { display: none !important; }
        main { padding-left: calc(240px + ${tokens.space.xl}px) !important; }
      }
      @media (max-width: ${tokens.breakpoint.lg - 1}px) {
        [data-menu-toggle] { display: inline-flex !important; }
      }
      @media (max-width: ${tokens.breakpoint.sm - 1}px) {
        [data-user-label] { display: none !important; }
        main { padding: ${tokens.space.lg}px !important; }
      }
    `;
    document.head.appendChild(tag);
  }, []);
  return null;
}

// ---------------------------------------------------------------------------
// Inline SVG icons (Lucide-style, currentColor fill)
// ---------------------------------------------------------------------------

const ICON: React.SVGProps<SVGSVGElement> = {
  width: 18,
  height: 18,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  viewBox: "0 0 24 24",
};

function IconDashboard() {
  return (
    <svg {...ICON}>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  );
}

function IconClients() {
  return (
    <svg {...ICON}>
      <circle cx="9" cy="8" r="3.25" />
      <path d="M2.75 19a6.25 6.25 0 0 1 12.5 0" />
      <circle cx="17" cy="9.5" r="2.5" />
      <path d="M14.5 19a4.5 4.5 0 0 1 6.75-3.9" />
    </svg>
  );
}

function IconDebtLookup() {
  return (
    <svg {...ICON}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function IconLogs() {
  return (
    <svg {...ICON}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8M8 17h5" />
    </svg>
  );
}

function IconLegal() {
  return (
    <svg {...ICON}>
      <path d="M12 3v18" />
      <path d="M5 7h14" />
      <path d="M7 7l-3 6a3 3 0 0 0 6 0z" />
      <path d="M17 7l-3 6a3 3 0 0 0 6 0z" />
      <path d="M8 21h8" />
    </svg>
  );
}

function IconSms() {
  return (
    <svg {...ICON}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M8 9h8M8 12.5h5" />
    </svg>
  );
}

function IconAutoSend() {
  return (
    <svg {...ICON}>
      <path d="M3 11l18-8-8 18-2.5-7.5L3 11z" />
      <path d="M10.5 13.5L21 3" />
    </svg>
  );
}

function IconDialer() {
  return (
    <svg {...ICON}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function IconLeadSales() {
  return (
    <svg {...ICON}>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 12h.01M18 12h.01" />
    </svg>
  );
}

function IconYes() {
  return (
    <svg {...ICON}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
      <path d="M8.5 11.5l2.5 2.5 4.5-4.5" />
    </svg>
  );
}

function IconMenu() {
  return (
    <svg {...ICON}>
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}

function IconClose() {
  return (
    <svg {...ICON}>
      <path d="M6 6l12 12M18 6l-12 12" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Route guards (section + admin)
//
// These sit INSIDE RequireAuth in the route table, which owns the /login
// redirect and holds the loading gate until /api/me resolves — so `user` is
// non-null by the time these render (the `!user` branch is defensive only).
// A user lacking the section is bounced to firstGrantedPath(user), i.e. a path
// they DO hold (or /no-access) — never a loop.
// ---------------------------------------------------------------------------

function RequireSection({
  section,
  children,
}: {
  section: Section;
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  if (!user) return null; // RequireAuth owns the redirect to /login
  if (user.isAdmin || user.sections.includes(section)) return <>{children}</>;
  return <Navigate to={firstGrantedPath(user)} replace />;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user) return null; // RequireAuth owns the redirect to /login
  if (user.isAdmin) return <>{children}</>;
  return <Navigate to={firstGrantedPath(user)} replace />;
}

// Resolves "/" and any unmatched path to the caller's first granted section
// (or /no-access). Always rendered inside RequireAuth, so `user` is present.
function RootRedirect() {
  const { user } = useAuth();
  if (!user) return null;
  return <Navigate to={firstGrantedPath(user)} replace />;
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell>
          <Suspense fallback={<div>Loading…</div>}>
            <Routes>
              <Route path="/login" element={<Login />} />
              {/* Landing: resolve "/" to the caller's first granted section
                  (admins → Debt Lookup; zero-grant users → NoAccess). */}
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <RootRedirect />
                  </RequireAuth>
                }
              />
              {/* Inbound RTB (GTI) dashboard — its surfaces keep their
                  /clients* paths; only the dashboard gets an explicit path.
                  BOTH prefixes gate on the inbound-rtb section (gotcha #2). */}
              <Route
                path="/inbound-rtb"
                element={
                  <RequireAuth>
                    <RequireSection section="inbound-rtb">
                      <Dashboard />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/clients"
                element={
                  <RequireAuth>
                    <RequireSection section="inbound-rtb">
                      <Clients />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/clients/new"
                element={
                  <RequireAuth>
                    <RequireSection section="inbound-rtb">
                      <ClientNew />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/clients/:id"
                element={
                  <RequireAuth>
                    <RequireSection section="inbound-rtb">
                      <ClientDetail />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/debt-lookup"
                element={
                  <RequireAuth>
                    <RequireSection section="debt-lookup">
                      <MirrorDashboard />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/debt-lookup/clients"
                element={
                  <RequireAuth>
                    <RequireSection section="debt-lookup">
                      <MirrorClients />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/debt-lookup/clients/new"
                element={
                  <RequireAuth>
                    <RequireSection section="debt-lookup">
                      <MirrorClientNew />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/debt-lookup/clients/:id"
                element={
                  <RequireAuth>
                    <RequireSection section="debt-lookup">
                      <MirrorClientDetail />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/logs"
                element={
                  <RequireAuth>
                    <RequireSection section="logs">
                      <Logs />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              {/* Reporting — GRT conversion postbacks table. */}
              <Route
                path="/grt-postback"
                element={
                  <RequireAuth>
                    <RequireSection section="reporting">
                      <GrtPostback />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              {/* Legal Widget — niche management (calls the legal.looprtb.com
                  admin API; same Cognito pool, so the id_token works). */}
              <Route
                path="/legal"
                element={
                  <RequireAuth>
                    <RequireSection section="legal-widget">
                      <LegalNiches />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/legal/new"
                element={
                  <RequireAuth>
                    <RequireSection section="legal-widget">
                      <LegalNicheNew />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/legal/:slug"
                element={
                  <RequireAuth>
                    <RequireSection section="legal-widget">
                      <LegalNicheEditor />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/legal/:slug/observability"
                element={
                  <RequireAuth>
                    <RequireSection section="legal-widget">
                      <LegalNicheObservability />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              {/* SMS — attribution dashboard + campaign management
                  (same admin API + Cognito pool). */}
              <Route
                path="/sms"
                element={
                  <RequireAuth>
                    <RequireSection section="sms">
                      <SmsDashboard />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/sms/auto-send"
                element={
                  <RequireAuth>
                    <RequireSection section="sms">
                      <SmsAutoSend />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/sms/campaigns"
                element={
                  <RequireAuth>
                    <RequireSection section="sms">
                      <SmsCampaigns />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/sms/campaigns/new"
                element={
                  <RequireAuth>
                    <RequireSection section="sms">
                      <SmsCampaignNew />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              <Route
                path="/sms/campaigns/:id"
                element={
                  <RequireAuth>
                    <RequireSection section="sms">
                      <SmsCampaignDetail />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              {/* Dialer — the TrackDrive callback-dialer admin surface (stats +
                  recent dials + operating config + on/off). Gated: sec-dialer. */}
              <Route
                path="/dialer"
                element={
                  <RequireAuth>
                    <RequireSection section="dialer">
                      <Dialer />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              {/* Lead Sales — the lead-sale proxy admin surface (config incl.
                  destinations/routing/exclusive-buyers + stats + recent sales +
                  on/off). Gated: sec-lead-sales. */}
              <Route
                path="/lead-sales"
                element={
                  <RequireAuth>
                    <RequireSection section="lead-sales">
                      <LeadSales />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              {/* Outbound — the Witzzy-first sequential-fallback admin surface
                  (nav label + header/subtitle read "Outbound" as of
                  witzzy_send_window_quota; route/page file/master-toggle
                  copy still say "Witzzy" — it names the partner). Config
                  incl. the webhook-URL paste + window/tier knobs + the
                  send-window/daily-quota editor + stats + the SEND kill
                  switch. Same section as Lead Sales: sec-lead-sales. */}
              <Route
                path="/lead-sales/witzzy"
                element={
                  <RequireAuth>
                    <RequireSection section="lead-sales">
                      <LeadSalesWitzzy />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              {/* Outbound calling — the "posted → called → revenue" report by
                  Pacific posting day (outbound_calling PRP v2). READ-ONLY: no
                  config, no on/off switch. Same section as Lead Sales:
                  sec-lead-sales. */}
              <Route
                path="/lead-sales/outbound"
                element={
                  <RequireAuth>
                    <RequireSection section="lead-sales">
                      <OutboundCalling />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              {/* HTM — the HTM (Pipes.ai) aged-lead resale admin surface
                  (htm_aged_leads PRP Task 7): enabled + test_mode toggles,
                  each behind its own confirm gate; the TEST MODE badge on the
                  stats strip; the config form with the write-only api-key
                  paste. Same section as Lead Sales: sec-lead-sales (the
                  admin-api routes sit under the existing
                  /^\/api\/lead-sales(\/|$)/ section policy). */}
              <Route
                path="/lead-sales/htm"
                element={
                  <RequireAuth>
                    <RequireSection section="lead-sales">
                      <LeadSalesHtm />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              {/* Yes — the conversational-iMessage engine admin surface (config
                  incl. campaigns + keywords + stats + recent conversations +
                  on/off). Gated: sec-yes. */}
              <Route
                path="/yes"
                element={
                  <RequireAuth>
                    <RequireSection section="yes">
                      <Yes />
                    </RequireSection>
                  </RequireAuth>
                }
              />
              {/* Admin-only — user management + global settings. */}
              <Route
                path="/users"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <Users />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/settings"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <Settings />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              {/* Zero-grant landing — no section guard (any authenticated user). */}
              <Route
                path="/no-access"
                element={
                  <RequireAuth>
                    <NoAccess />
                  </RequireAuth>
                }
              />
              {/* Unknown path → first granted section (or NoAccess). */}
              <Route
                path="*"
                element={
                  <RequireAuth>
                    <RootRedirect />
                  </RequireAuth>
                }
              />
            </Routes>
          </Suspense>
        </AppShell>
      </AuthProvider>
    </BrowserRouter>
  );
}
